// src/components/HandicapApproval.js
import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { collection, getDocs, updateDoc, doc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase-config";

const HandicapApproval = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionMessage, setActionMessage] = useState("");
  // State for image dialog
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState("");
  // State for editable notes per request (object: id → notes)
  const [notesById, setNotesById] = useState({});

  // Helper: Format Firestore timestamp.
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "";
    if (typeof timestamp === "object" && timestamp.seconds) {
      return new Date(timestamp.seconds * 1000).toLocaleString();
    } else if (typeof timestamp === "number") {
      return new Date(timestamp).toLocaleString();
    }
    return "";
  };

  useEffect(() => {
    const fetchRequests = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "handicapRequests"));
        const reqs = querySnapshot.docs.map((docSnap) => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            ...data,
          };
        });
        // Sort requests: pending first (older pending first), then by timestamp descending.
        reqs.sort((a, b) => {
          const tsA = a.timestamp && a.timestamp.seconds ? a.timestamp.seconds : a.timestamp || 0;
          const tsB = b.timestamp && b.timestamp.seconds ? b.timestamp.seconds : b.timestamp || 0;
          if (a.status === "pending" && b.status === "pending") {
            return tsA - tsB;
          } else if (a.status !== "pending" && b.status !== "pending") {
            return tsB - tsA;
          } else if (a.status === "pending") {
            return -1;
          } else {
            return 1;
          }
        });
        setRequests(reqs);
        // Initialize notesById from existing request notes.
        const notesInit = {};
        reqs.forEach((req) => {
          notesInit[req.id] = req.notes || "";
        });
        setNotesById(notesInit);
      } catch (error) {
        console.error("Error fetching handicap requests:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchRequests();
  }, []);

  // Updated handleApprove to also update the user's handicap field.
  const handleApprove = async (req) => {
    try {
      // Update the handicap request document.
      await updateDoc(doc(db, "handicapRequests", req.id), {
        status: "approved",
        timestamp: serverTimestamp(),
      });
      // Update the user's document with the new handicap.
      await updateDoc(doc(db, "users", req.userId), {
        handicap: req.requestedHandicap,
      });
      setActionMessage(`Request for ${req.name} approved.`);
      setRequests((prev) =>
        prev.map((r) =>
          r.id === req.id
            ? { ...r, status: "approved", timestamp: new Date().getTime(), currentHandicap: req.requestedHandicap }
            : r
        )
      );
    } catch (error) {
      console.error("Error approving request:", error);
      setActionMessage("Failed to approve request.");
    }
  };

  const handleReject = async (req) => {
    try {
      await updateDoc(doc(db, "handicapRequests", req.id), {
        status: "rejected",
        timestamp: serverTimestamp(),
      });
      setActionMessage(`Request for ${req.name} rejected.`);
      setRequests((prev) =>
        prev.map((r) =>
          r.id === req.id
            ? { ...r, status: "rejected", timestamp: new Date().getTime() }
            : r
        )
      );
    } catch (error) {
      console.error("Error rejecting request:", error);
      setActionMessage("Failed to reject request.");
    }
  };

  // Save updated notes for a given request.
  const handleSaveNotes = async (reqId) => {
    try {
      await updateDoc(doc(db, "handicapRequests", reqId), {
        notes: notesById[reqId],
        timestamp: serverTimestamp(),
      });
      setActionMessage("Notes updated successfully.");
      // Optionally update local copy.
      setRequests((prev) =>
        prev.map((r) => (r.id === reqId ? { ...r, notes: notesById[reqId] } : r))
      );
    } catch (error) {
      console.error("Error updating notes:", error);
      setActionMessage("Failed to update notes.");
    }
  };

  const handleImageClick = (url) => {
    setSelectedImageUrl(url);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedImageUrl("");
  };

  const buttonSx = { mr: 1, minWidth: 100 };

  if (loading) {
    return (
      <Box sx={{ textAlign: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ overflowX: "auto", mb: 4, p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Handicap Approval
      </Typography>
      {actionMessage && (
        <Typography variant="body1" color="primary" sx={{ mb: 2 }}>
          {actionMessage}
        </Typography>
      )}
      <TableContainer>
        <Table aria-label="admin dashboard table">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Current Handicap</TableCell>
              <TableCell>Requested Handicap</TableCell>
              <TableCell>Images</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell>Timestamp</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requests.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No handicap requests.
                </TableCell>
              </TableRow>
            )}
            {requests.map((req) => (
              <TableRow key={req.id}>
                <TableCell>{req.name}</TableCell>
                <TableCell>{req.currentHandicap}</TableCell>
                <TableCell>{req.requestedHandicap}</TableCell>
                <TableCell>
                  {req.imageUrls && req.imageUrls.length > 0 ? (
                    req.imageUrls.map((url, index) => (
                      <Box
                        key={index}
                        component="img"
                        src={url}
                        alt={`handicap ${index}`}
                        onClick={() => handleImageClick(url)}
                        sx={{
                          height: 50,
                          width: 50,
                          objectFit: "cover",
                          mr: 1,
                          cursor: "pointer",
                        }}
                      />
                    ))
                  ) : (
                    "No images"
                  )}
                </TableCell>
                <TableCell>{req.status}</TableCell>
                <TableCell>
                  <TextField
                    value={notesById[req.id] || ""}
                    onChange={(e) =>
                      setNotesById((prev) => ({ ...prev, [req.id]: e.target.value }))
                    }
                    fullWidth
                    variant="standard"
                  />
                </TableCell>
                <TableCell>{formatTimestamp(req.timestamp)}</TableCell>
                <TableCell>
                  {req.status === "pending" && (
                    <>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => handleApprove(req)}
                        sx={buttonSx}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={() => handleReject(req)}
                        sx={buttonSx}
                      >
                        Reject
                      </Button>
                    </>
                  )}
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleSaveNotes(req.id)}
                    sx={buttonSx}
                  >
                    Save Notes
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          View Image
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box
            component="img"
            src={selectedImageUrl}
            alt="Enlarged view"
            sx={{ width: "100%", height: "auto" }}
          />
        </DialogContent>
      </Dialog>
    </Paper>
  );
};

export default HandicapApproval;